/**
 * Simplified memory cache handler
 */

import { EventEmitter } from '../base/event-emitter.js';
import { ISyncHandler } from '../interfaces/handler.interface.js';
import {
  IStatsProvider,
  ISyncRepository,
} from '../interfaces/repository.interface.js';
import type { TranslationData } from '../types/translation-data.types.js';
import type {
  TranslationContext,
  TranslationRequest,
  TranslationResult,
} from '../types/translation.types.js';
import { logger } from '../utils/index.js';
import {
  createNamespaceCacheKey,
  flattenTranslations,
  resolveNestedKey,
} from '../utils/translation-value-utils.js';

/**
 * Parsed cache entry containing both text and styles
 */
interface CachedContentUnit {
  text: string;
  styles: Record<string, string[]>;
}

/**
 * Simple memory cache handler
 */
export class SyncHandler implements ISyncHandler {
  readonly name: string;
  readonly version = '1.0.0';
  readonly priority: number;
  readonly type = 'memory' as const;
  readonly enabled: boolean;
  private ttlMap = new Map<string, number>();
  private ttl: number;
  private repository: ISyncRepository & IStatsProvider;
  private bus: EventEmitter;

  constructor(
    eventEmitter: EventEmitter,
    repository: ISyncRepository & IStatsProvider
  ) {
    this.bus = eventEmitter;
    this.repository = repository;
    this.name = repository.name;
    const config = repository.getConfig();
    this.enabled = config.enabled ?? true;
    this.priority = config.priority ?? 0;
    this.ttl = config.options?.ttl ?? 30 * 60 * 1000; // Default 30 minutes
  }

  getSourceType(): 'memory' | 'storage' | 'network' | 'fallback' {
    return 'memory';
  }

  /**
   * Simple lookup implementation
   * Returns ContentUnit with both text and styles
   */
  lookup(request: TranslationRequest): TranslationResult {
    const startTime = Date.now();
    logger.debug(`Sync lookup: ${request.cleanKey}`);
    const repoBus = this.bus.repo(request, this.name);
    repoBus.getStart();

    const directResult = this.tryDirectLookup(request);
    if (directResult !== null) {
      logger.debug(`Cache hit: ${request.cleanKey}`);
      repoBus.getSuccess(directResult.text);
      return this.createResult(directResult, 'memory', 200, startTime, true);
    }

    // Try namespace lookup for nested keys
    if (request.cleanKey.includes('.') && request.namespace) {
      const namespaceResult = this.tryNamespaceLookup(request);
      if (namespaceResult !== null) {
        logger.debug(`Namespace hit: ${request.cleanKey}`);
        repoBus.getSuccess(namespaceResult.text);
        return this.createResult(
          namespaceResult,
          'memory',
          200,
          startTime,
          true
        );
      }
    }

    logger.debug(`Cache miss: ${request.cleanKey}`);
    repoBus.getMiss();

    // Return fallback result (no styles for fallback)
    return this.createResult(
      { text: request.parsedDefaultValue || request.cleanKey, styles: {} },
      'fallback',
      404,
      startTime
    );
  }

  /**
   * Simple save implementation
   * Stores ContentUnit (text + styles) as JSON
   */
  save(request: TranslationRequest, result: TranslationResult): void {
    if (!result.value) return;
    const repoBus = this.bus.repo(request, this.name);

    // Store as ContentUnit JSON (text + styles together)
    const contentUnit: CachedContentUnit = {
      text: result.value,
      styles: result.styles || {},
    };
    this.setWithTTL(request.individualCacheKey, JSON.stringify(contentUnit));

    logger.debug(`Saved to cache: ${request.cleanKey}`);
    repoBus.setSuccess(result.value);
  }

  /**
   * Namespace saving implementation
   */
  saveNamespace(
    translations: TranslationData,
    context: TranslationContext
  ): void {
    if (!context.namespace) return;
    const repoBus = this.bus.repo(
      context.locale + ':' + context.namespace,
      this.name
    );

    // Store full namespace: @locale:namespace
    const namespaceCacheKey = createNamespaceCacheKey(
      context.locale,
      context.namespace
    );
    this.setWithTTL(namespaceCacheKey, JSON.stringify(translations));

    // Store flattened individual keys: locale:namespace:key
    const flattened = flattenTranslations(
      translations,
      '',
      context.locale,
      context.namespace!
    );

    for (const [flatKey, flatValue] of Object.entries(flattened)) {
      this.setWithTTL(flatKey, flatValue);
    }
    repoBus.setSuccess(JSON.stringify(translations));
  }

  /**
   * ✅ Strategy 1: Direct cache lookup using unified pattern
   * Returns parsed ContentUnit (text + styles) or null
   */
  private tryDirectLookup(request: TranslationRequest): CachedContentUnit | null {
    // Check TTL and expire if needed
    if (this.checkAndExpireTTL(request.individualCacheKey)) {
      return null;
    }

    const cachedValue = this.repository.get(request.individualCacheKey);
    if (!cachedValue) return null;

    this.refreshTTL(request.individualCacheKey); // Refresh TTL on access

    // Parse cached ContentUnit JSON
    try {
      const parsed = JSON.parse(cachedValue);
      if (typeof parsed === 'object' && 'text' in parsed) {
        return {
          text: String(parsed.text),
          styles: parsed.styles || {},
        };
      }
      // Legacy format: plain string (shouldn't happen with new data)
      return { text: cachedValue, styles: {} };
    } catch {
      // If parse fails, treat as legacy plain string
      return { text: cachedValue, styles: {} };
    }
  }

  /**
   * ✅ Strategy 2: Namespace lookup using unified pattern
   * Returns parsed ContentUnit (text + styles) or null
   */
  private tryNamespaceLookup(request: TranslationRequest): CachedContentUnit | null {
    if (!request.namespace) return null;

    const namespaceCacheKey = createNamespaceCacheKey(
      request.locale,
      request.namespace
    );

    // Check TTL for namespace
    if (this.checkAndExpireTTL(namespaceCacheKey)) {
      return null;
    }

    const namespaceData = this.repository.get(namespaceCacheKey);
    if (!namespaceData) return null;
    this.refreshTTL(namespaceCacheKey); // Refresh TTL on access

    try {
      const translations = JSON.parse(namespaceData);
      const value = resolveNestedKey(translations, request.cleanKey);

      if (value !== null && value !== undefined) {
        // Extract ContentUnit from namespace data
        let contentUnit: CachedContentUnit;

        if (typeof value === 'object' && value !== null && 'text' in value) {
          // Server ContentUnit format: { text, styles }
          contentUnit = {
            text: String((value as { text: unknown }).text),
            styles: (value as { styles?: Record<string, string[]> }).styles || {},
          };
        } else {
          // Plain string fallback
          contentUnit = { text: String(value), styles: {} };
        }

        // Cache the individual key for faster future access (as JSON)
        this.setWithTTL(request.individualCacheKey, JSON.stringify(contentUnit));

        logger.debug(`Cached from namespace: ${request.cleanKey}`);
        return contentUnit;
      }
    } catch (error) {
      // Invalid JSON in namespace cache
      logger.debug(
        `Invalid JSON in namespace cache: ${namespaceCacheKey}`,
        error
      );
      this.repository.remove(namespaceCacheKey);
      this.ttlMap.delete(namespaceCacheKey);
    }

    return null;
  }

  /**
   * Helper to create consistent TranslationResult objects
   * Now includes styles from ContentUnit
   */
  private createResult(
    contentUnit: CachedContentUnit,
    source: 'memory' | 'storage' | 'network' | 'fallback',
    status: number,
    startTime: number,
    cached?: boolean
  ): TranslationResult {
    return {
      value: contentUnit.text,
      styles: contentUnit.styles,
      source,
      status,
      duration: Date.now() - startTime,
      ...(cached !== undefined && { cached }),
    };
  }

  /**
   * Check TTL and expire if needed
   * Returns true if expired, false if still valid
   */
  private checkAndExpireTTL(cacheKey: string): boolean {
    const timestamp = this.ttlMap.get(cacheKey);
    if (timestamp && Date.now() - timestamp > this.ttl) {
      this.repository.remove(cacheKey);
      this.ttlMap.delete(cacheKey);
      logger.debug(`TTL expired: ${cacheKey}`);
      return true;
    }
    return false;
  }

  /**
   * Set value in repository and update TTL
   */
  private setWithTTL(cacheKey: string, value: string): void {
    this.repository.set(cacheKey, value);
    this.ttlMap.set(cacheKey, Date.now());
  }

  /**
   * Refresh TTL on access
   */
  private refreshTTL(cacheKey: string): void {
    this.ttlMap.set(cacheKey, Date.now());
  }

  /**
   * Get TTL information for debugging
   */
  getTTLInfo(): Record<
    string,
    { timestamp: number; age: number; remaining: number }
  > {
    const ttlInfo: Record<
      string,
      { timestamp: number; age: number; remaining: number }
    > = {};
    const now = Date.now();

    for (const [key, timestamp] of this.ttlMap.entries()) {
      const age = now - timestamp;
      const remaining = Math.max(0, this.ttl - age);
      ttlInfo[key] = { timestamp, age, remaining };
    }

    return ttlInfo;
  }

  getEntries() {
    if (this.repository.getEntries !== undefined) {
      return this.repository.getEntries();
    }
  }

  /**
   * Clear all entries for a specific namespace
   * Removes namespace cache entry, individual keys, and TTL entries
   */
  clearNamespace(locale: string, namespace: string): void {
    const namespaceCacheKey = createNamespaceCacheKey(locale, namespace);
    const keyPrefix = `${locale}:${namespace}:`;

    // Remove namespace cache entry
    this.repository.remove(namespaceCacheKey);
    this.ttlMap.delete(namespaceCacheKey);

    // Remove individual translation keys and their TTL entries
    const entries = this.repository.getEntries?.();
    if (entries) {
      for (const key of Object.keys(entries)) {
        if (key.startsWith(keyPrefix)) {
          this.repository.remove(key);
          this.ttlMap.delete(key);
        }
      }
    }

    logger.debug(`Cleared namespace: ${locale}:${namespace}`);
  }

  destroy(): void {
    this.repository.clear();
    this.ttlMap.clear();
  }

  /**
   * Direct cache set (for optimistic updates)
   * Bypasses event emission for direct cache manipulation
   * Stores as ContentUnit JSON (text + styles)
   */
  setCache(key: string, value: string, styles?: Record<string, string[]>): void {
    const contentUnit: CachedContentUnit = {
      text: value,
      styles: styles || {},
    };
    this.setWithTTL(key, JSON.stringify(contentUnit));
    logger.debug(`Direct cache set: ${key}`);
  }
}
